import React from "react";
import { useRef, useEffect, useState } from "react";
import { FaLongArrowAltLeft, FaLongArrowAltRight } from "react-icons/fa";
import { IoMdMenu } from "react-icons/io";
import { MdEmail } from "react-icons/md";
import { LuEuro, LuChevronDown, LuPlus, LuHouse, LuBuilding2, LuUser, LuBell, LuBellRing, LuStore } from "react-icons/lu";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../sidebar";
import LanguageSwitcher from "../../../LanguageSwitcher";
import { isGuestMode } from "../../../methods/guestMode";
import { isApp } from "../../../native/platform";
import { isWhiteLabelHost } from "../../../providers/WhiteLabel/whiteLabelCopy";
import { useSelector } from "react-redux";
import { propertyLimitReached } from "../../../models/propertyLimit";
import PropertyCheck from "../../../Pages/propertySteps/PropertyCheck";

const Html = ({
  isOpen,
  toggle,
  isOpen1,
  messageCount,
  messageBlink,
  notificationCount,
  onOpenMessages,
  showAccountMenu,
  setShowAccountMenu,
  user,
  isMobileView,
  hasSidebar,
  onOpenMobileSidebar,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const activePlan = useSelector((state) => state.activePlan);
  const [planModal, setPlanModal] = useState(false);

  // Création d'un bien : on affiche la modale de limite AVANT de naviguer
  // si le nombre maximal de biens du plan est déjà atteint.
  const handleCreateProperty = () => {
    if (user?.isGuest || isGuestMode()) {
      navigate("/signup");
      return;
    }
    if (propertyLimitReached(activePlan)) {
      setPlanModal(true);
      return;
    }
    navigate("/property1");
  };

  // Plateforme marque blanche (sous-domaine d'agence) ou membre rattaché à une
  // agence : dans ces contextes, le bouton « Offre pro » (AnyHomes) est masqué.
  const isWhiteLabelContext = isWhiteLabelHost() || Boolean(user?.whiteLabelAgencyId);

  // Webview applicative (app mobile) : le header « inapp » doit montrer
  // Offre pro + Parrainage même en mode invité et sur mobile.
  const isAppView =
    new URLSearchParams(window.location.search).get("isApp") === "1" ||
    new URLSearchParams(window.location.search).get("IsApp") === "1" ||
    (typeof sessionStorage !== "undefined" && sessionStorage.getItem("isAppView") === "1");

  // Espace application connecté : un pro (agence, agent, chasseur, investisseur)
  // disposant d'un abonnement payant n'a plus besoin du bouton « Offre pro ».
  const headerRole = String(user?.role || "").toLowerCase();
  const isProUser =
    (user?.accountType === "pro" || !!user?.agencyOwnerId) &&
    ["agency", "agent", "hunter", "investor"].includes(headerRole);
  // Chasseur immobilier : on masque les CTA « Services immo à la carte » et
  // « Créer le profil de mon bien ».
  const isHunter = headerRole === "hunter";
  const hasPaidPlan =
    user?.planType === "paid" ||
    (user?.planId && user.planId.planType === "paid") ||
    activePlan?.activePlan?.[0]?.planType === "paid";
  const hideOfferProForPaidPro = isProUser && hasPaidPlan;

  const accountMenuRef = useRef(null);
  const offersMenuRef = useRef(null);
  const [showOffersMenu, setShowOffersMenu] = useState(false);
  useEffect(() => {
    if (!showAccountMenu) return;
    const handler = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showAccountMenu, setShowAccountMenu]);

  useEffect(() => {
    if (!showOffersMenu) return;
    const handler = (e) => {
      if (offersMenuRef.current && !offersMenuRef.current.contains(e.target)) {
        setShowOffersMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showOffersMenu]);

  const getLogo = () => {
    return "/assets/img/logo.png";
  };

  // Largeur du header selon l’état de la sidebar :
  // - mobile (< 768px) ou pas de sidebar : pleine largeur
  // - sidebar repliée (70px) : calc(100% - 70px)
  // - sidebar déployée (280px) : calc(100% - 280px)
  let headerWidthClass = "w-full";
  if (hasSidebar && !isMobileView) {
    headerWidthClass = isOpen ? "w-[calc(100%-280px)]" : "w-[calc(100%-70px)]";
  }

  return (
    <nav
      className={`min-sidebar ${headerWidthClass} shadow-btn py-1.5 bg-white border-b fixed ml-auto right-0 z-40 flex items-center h-[71px] !px-2 md:!px-5`}
      style={{ transition: 'width 0.3s ease, transform 0.3s ease', transform: 'var(--header-ty, translateY(0))' }}
    >

      {/* Zone gauche : hamburger + actions */}
      <div className="flex items-center gap-1 sm:gap-2 min-w-0 shrink-0">
      {/* Hamburger — mobile uniquement, ouvre l’overlay de navigation */}
      {hasSidebar && isMobileView && (
        <button
          onClick={onOpenMobileSidebar}
          className="mr-3 p-2 rounded-lg text-[var(--brand,#976DD0)] hover:bg-[rgba(var(--brand-rgb,151,109,208),0.13)] transition-colors"
          aria-label="Ouvrir le menu"
          title="Menu"
        >
          <IoMdMenu size={24} />
        </button>
      )}

      {/* CTA création de bien — desktop uniquement (mobile : demi-cercle au-dessus du footer menu) */}
      {!isHunter && (
      <button
        onClick={handleCreateProperty}
        className="hidden lg:inline-flex ml-2 sm:ml-8 bg-[var(--brand,#976DD0)] hover:bg-[var(--brand-hover,#7D55B5)] text-white text-[13px] font-semibold px-2.5 sm:px-4 py-2 rounded-full transition-colors shadow-sm items-center justify-center gap-1.5 shrink-0"
        title={user?.isGuest || isGuestMode() ? t("buttons.signUp") : (user?.accountType === 'pro' ? t("buttons.createPropertyPro") : t("buttons.createProperty"))}
      >
        <span className="sm:hidden inline-flex items-center gap-1">
          <LuHouse className="w-4 h-4" aria-hidden="true" />
          <LuPlus className="w-3.5 h-3.5" aria-hidden="true" />
        </span>
        <span className="hidden sm:inline whitespace-nowrap">
          {user?.isGuest || isGuestMode() ? t("buttons.signUp") : (user?.accountType === 'pro' ? t("buttons.createPropertyPro") : t("buttons.createProperty"))}
        </span>
      </button>
      )}

      {/* Services immo à la carte — header de la plateforme (hors marketing),
          desktop, loggué ou non. */}
      {!isHunter && (
        <button
          onClick={() => navigate('/marketplace')}
          className="hidden md:inline-flex ml-2 sm:ml-3 bg-white hover:bg-[#F3ECFF] text-[var(--brand,#976DD0)] text-[13px] font-semibold px-2.5 sm:px-4 py-2 rounded-full transition-colors border border-[var(--brand,#976DD0)] shadow-sm items-center justify-center gap-1.5 shrink-0"
          title={t('header.marketplaceServices', 'Services immo à la carte')}
        >
          <LuStore className="text-[15px] shrink-0" />
          <span className="hidden sm:inline whitespace-nowrap">{t('header.marketplaceServices', 'Services immo à la carte')}</span>
        </button>
      )}

      {/* Parrainage — connecté : tableau de bord (/invite) ; guest : présentation (/parrainage).
          Sur mobile il est dans le menu hamburger (sidebar) sauf en vue inapp (app mobile).
          Masqué dans l'app native (pas d'environnement marketing). */}
      {!isApp() && (
      <button
        onClick={() => navigate(user?.loggedIn && !user?.isGuest && !isGuestMode() ? '/invite?source=header' : '/parrainage')}
        className={isAppView ? "inline-flex ml-2 sm:ml-3 bg-white hover:bg-[#F3ECFF] text-[var(--brand,#976DD0)] text-[13px] font-semibold px-2.5 sm:px-4 py-2 rounded-full transition-colors border border-[var(--brand,#976DD0)] shadow-sm items-center justify-center gap-1.5 shrink-0" : "hidden md:inline-flex ml-2 sm:ml-3 bg-white hover:bg-[#F3ECFF] text-[var(--brand,#976DD0)] text-[13px] font-semibold px-2.5 sm:px-4 py-2 rounded-full transition-colors border border-[var(--brand,#976DD0)] shadow-sm items-center justify-center gap-1.5 shrink-0"}
        title={t('header.referral')}
      >
        <LuEuro className="text-[15px] shrink-0" />
        <span className="hidden sm:inline whitespace-nowrap">{t('header.referral')}</span>
      </button>
      )}
      </div>

      {/* Logo AnyHomes centré — mobile uniquement. Clic → retour en haut de l'écran. */}
      <div className="flex-1 flex justify-center px-2 min-w-0 lg:hidden">
        <button
          type="button"
          onClick={() => {
            const el = document.querySelector(".page-content-wrapper");
            if (el) el.scrollTo({ top: 0, behavior: "smooth" });
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          aria-label="Revenir en haut"
          className="bg-transparent border-0 p-0 cursor-pointer"
        >
          <img
            src="/assets/img/anyhomes-full-logo.png"
            alt="AnyHomes"
            className="h-[48px] sm:h-[56px] w-auto max-w-[55vw] object-contain"
          />
        </button>
      </div>

      <div className="flex items-center gap-1 sm:gap-4 min-w-0 shrink-0 lg:ml-auto">
        {!isWhiteLabelContext && !isApp() && !hideOfferProForPaidPro && (
          <div className={`relative shrink-0 ${isAppView ? "" : "hidden md:block"}`} ref={offersMenuRef}>
            <button
              onClick={() => setShowOffersMenu((v) => !v)}
              className="inline-flex items-center justify-center gap-1 bg-[rgba(var(--brand-rgb,151,109,208),0.12)] hover:bg-[rgba(var(--brand-rgb,151,109,208),0.22)] text-[var(--brand,#7E54BD)] text-[13px] font-semibold px-2.5 sm:px-4 py-2 rounded-full transition-colors"
              title={t('header.offerPro', 'Offre pro')}
            >
              <span className="hidden sm:inline">{t('header.offerPro', 'Offre pro')}</span>
              <LuBuilding2 className="w-4 h-4 sm:hidden" aria-hidden="true" />
              <LuChevronDown className="w-3.5 h-3.5" />
            </button>
            {showOffersMenu && (
              <div className="absolute right-0 top-full mt-2 w-72 rounded-lg bg-white border border-gray-100 shadow-lg py-2 z-50">
                <button
                  onClick={() => { navigate('/offers-pro-agent'); setShowOffersMenu(false); }}
                  className="block w-full text-left px-4 py-2 text-[13px] text-[#47525E] hover:bg-[rgba(var(--brand-rgb,151,109,208),0.1)] hover:text-[var(--brand,#976DD0)]"
                >
                  {t('header.offerProAgent', 'Agents indépendants')}
                </button>
                <button
                  onClick={() => { navigate('/offers-pro'); setShowOffersMenu(false); }}
                  className="block w-full text-left px-4 py-2 text-[13px] text-[#47525E] hover:bg-[rgba(var(--brand-rgb,151,109,208),0.1)] hover:text-[var(--brand,#976DD0)]"
                >
                  {t('header.offerProAgency', 'Agences immobilières')}
                </button>
                <button
                  onClick={() => { navigate('/offers-pro-investor'); setShowOffersMenu(false); }}
                  className="block w-full text-left px-4 py-2 text-[13px] text-[#47525E] hover:bg-[rgba(var(--brand-rgb,151,109,208),0.1)] hover:text-[var(--brand,#976DD0)]"
                >
                  {t('header.offerProInvestor', "Investisseurs Immobiliers")}
                </button>
                <button
                  onClick={() => { navigate('/offers-pro-hunter'); setShowOffersMenu(false); }}
                  className="block w-full text-left px-4 py-2 text-[13px] text-[#47525E] hover:bg-[rgba(var(--brand-rgb,151,109,208),0.1)] hover:text-[var(--brand,#976DD0)]"
                >
                  {t('header.offerProHunter', 'Chasseur immobilier')}
                </button>
                <button
                  onClick={() => { navigate('/offers-pro-diagnostiqueur'); setShowOffersMenu(false); }}
                  className="block w-full text-left px-4 py-2 text-[13px] text-[#47525E] hover:bg-[rgba(var(--brand-rgb,151,109,208),0.1)] hover:text-[var(--brand,#976DD0)]"
                >
                  {t('header.offerProDiagnostic', 'Diagnostiqueurs immobiliers')}
                </button>
                <button
                  onClick={() => { navigate('/offers-pro-decorateur'); setShowOffersMenu(false); }}
                  className="block w-full text-left px-4 py-2 text-[13px] text-[#47525E] hover:bg-[rgba(var(--brand-rgb,151,109,208),0.1)] hover:text-[var(--brand,#976DD0)]"
                >
                  {t('header.offerProDecorator', "Décorateurs d'intérieur")}
                </button>
              </div>
            )}
          </div>
        )}
        <div className="hidden lg:block"><LanguageSwitcher /></div>
        {(user?.isGuest || isGuestMode()) && (
          <>
            <button
              onClick={() => navigate('/login')}
              className="hidden sm:inline-flex text-[13px] text-gray-700 hover:text-[var(--brand,#976DD0)]"
            >
              {t('authentication.login', 'Connexion')}
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="inline-flex items-center bg-[var(--brand,#976DD0)] hover:bg-[var(--brand-hover,#7E54BD)] text-white text-[13px] font-medium rounded-full px-2.5 sm:px-4 py-2 transition shrink-0 whitespace-nowrap"
            >
              {t('authentication.signup', "S'inscrire")}
            </button>
          </>
        )}
        {!user?.isGuest && !isGuestMode() && Number(messageCount) > 0 && (
          <button
            className={`mx-0 sm:mx-2 shrink-0 message-header-btn ${messageBlink ? "animate-blink" : ""}`}
            title="Messages"
            onClick={() => (onOpenMessages ? onOpenMessages() : navigate('/chat'))}
          >
            <MdEmail className="menu-icon violet-message" size={22} />
          </button>
        )}
        {user?.loggedIn && !user?.isGuest && !isGuestMode() && (
          <button
            className="mx-1 sm:mx-2 notification-btn shrink-0"
            title="Notifications"
            onClick={() => navigate('/notifications')}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              padding: '4px',
            }}
          >
            {Number(notificationCount) > 0 ? (
              <LuBellRing
                className="menu-icon"
                size={24}
                style={{ color: 'var(--brand,#976DD0)' }}
              />
            ) : (
              <LuBell
                className="menu-icon"
                size={24}
                style={{ color: '#C7C7CC' }}
              />
            )}
          </button>
        )}
        {!user?.isGuest && !isGuestMode() && (
          <div ref={accountMenuRef} style={{position: 'relative', display: 'inline-block'}} className="shrink-0">
            <button className="mx-1 sm:mx-2" title="Account" onClick={() => setShowAccountMenu((v) => !v)}>
              <LuUser className="menu-icon" size={22} />
            </button>
            {showAccountMenu &&
              <div style={{position: 'absolute', right: 0, top: '100%', zIndex: 100}}>
                {/* Le menu utilisateur sera injecté ici par PageLayout */}
                {window.renderAccountMenu && window.renderAccountMenu()}
              </div>
            }
          </div>
        )}
      </div>

      {isOpen1 && (
        <div className="w-100 mobi-dropdown">
          <Sidebar />
        </div>
      )}
      <PropertyCheck planModal={planModal} setplanModal={setPlanModal} />
    </nav>
  );
};

export default React.memo(Html);
